// Stable per-browser ``client_id`` used for chat-history attribution and
// authorization. Persisted in both localStorage (durable across sessions in
// the same browser) and a cookie (so direct fetches without JS can also
// surface it as a fallback).
//
// We do NOT treat this as authentication — it's a soft identifier. Real auth
// is handled by Django sessions; this only scopes "which chats are mine?"
// and "which feedback rows are mine?" within a single browser profile.

const KEY = 'malpedia-client-id';
const COOKIE_NAME = 'client_id';
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365 * 5; // 5 years

function readCookie(name: string): string | null {
  const prefix = `${name}=`;
  for (const part of document.cookie.split(';')) {
    const trimmed = part.trim();
    if (trimmed.startsWith(prefix)) {
      return decodeURIComponent(trimmed.slice(prefix.length));
    }
  }
  return null;
}

function writeCookie(name: string, value: string): void {
  const attrs = [
    `${name}=${encodeURIComponent(value)}`,
    'Path=/',
    `Max-Age=${COOKIE_MAX_AGE}`,
    'SameSite=Lax',
  ];
  if (window.location.protocol === 'https:') attrs.push('Secure');
  document.cookie = attrs.join('; ');
}

let cached: string | null = null;

export function getClientId(): string {
  if (cached) return cached;

  const fromStorage = (() => {
    try {
      return localStorage.getItem(KEY);
    } catch {
      return null;
    }
  })();
  const fromCookie = readCookie(COOKIE_NAME);

  let value = fromStorage || fromCookie;
  if (!value) {
    value = crypto.randomUUID();
  }

  // Re-sync both stores so they stay in lockstep (covers cookie purges and
  // localStorage wipes).
  try {
    localStorage.setItem(KEY, value);
  } catch {
    /* private mode etc — cookie alone is acceptable */
  }
  writeCookie(COOKIE_NAME, value);

  cached = value;
  return value;
}
